import React from 'react';
import { FieldSet, SidePanel, Thumbnail } from '@wix/design-system';
import { useTranslation } from '@wix/yoshi-flow-editor';
import { useStyles } from '@wix/tpa-settings/react';
import layoutParams from '../../../layoutParams';
import { dataHooks } from '../../../../../constants';
import { SocialFirstPresetIllustration } from '../../../../../assets/icons/SocialFirstPresetIllustration';
import { SocialLastPresetIllustration } from '../../../../../assets/icons/SocialLastPresetIllustration';
import {
  IdentityFormLayoutAbove,
  IdentityFormLayoutBelow,
} from '@wix/design-system-illustrations';
import { SocialButtonsPlacement } from '../../../../../constants/layoutConstants';
import { useStudio2WdsThumbnailSettings } from '../../../../common/AuthThumbnail/useStudio2WdsThumbnailSettings';

const THUMBNAIL_SIZE = 114;

export { SocialButtonsPlacement };

export const SocialPreset: React.FC = () => {
  const { t } = useTranslation();
  const styles = useStyles();
  const {
    isEnabled: isStudio2WdsThumbnailEnabled,
    skin: thumbnailSkin,
    thumbnailSize,
    illustrationSize,
    fieldSetLayout,
  } = useStudio2WdsThumbnailSettings(THUMBNAIL_SIZE);
  // Legacy: `auto auto` (unchanged); Studio 2: two fixed 72px columns, one gap.
  const { columns, gap } = fieldSetLayout(2, 'auto auto');

  // Studio 2 + FT renders the published WDS artwork ("Social above" = the
  // illustration with the social squares ON TOP); Harmony keeps the legacy assets.
  const FirstIllustration = isStudio2WdsThumbnailEnabled
    ? IdentityFormLayoutAbove
    : SocialFirstPresetIllustration;
  const LastIllustration = isStudio2WdsThumbnailEnabled
    ? IdentityFormLayoutBelow
    : SocialLastPresetIllustration;

  const socialButtonsFirst =
    styles.get(layoutParams.socialButtonsFirst) || SocialButtonsPlacement.FIRST;

  const onSocialPresetChange = (preset: SocialButtonsPlacement) => {
    styles.set(layoutParams.socialButtonsFirst, preset);
  };

  return (
    <SidePanel.Field divider={false}>
      <FieldSet
        legend={t(
          'memberAuthentication.loginForm.settingsPanel.layout.layoutPreset.title',
        )}
        direction="horizontal"
        columns={columns}
        gap={gap}
      >
        <Thumbnail
          dataHook={dataHooks.layoutTab.socialFirstThumbnail}
          size="tiny"
          selected={socialButtonsFirst === SocialButtonsPlacement.FIRST}
          onClick={() => onSocialPresetChange(SocialButtonsPlacement.FIRST)}
          hideSelectedIcon
          height={thumbnailSize}
          width={thumbnailSize}
          noPadding
          image={
            <FirstIllustration
              width={illustrationSize}
              height={illustrationSize}
              data-illustration={
                isStudio2WdsThumbnailEnabled
                  ? 'IdentityFormLayoutAbove'
                  : undefined
              }
            />
          }
          title={t(
            'memberAuthentication.loginForm.settingsPanel.layout.layoutPreset.socialFirst',
          )}
          textPosition="outside"
          skin={thumbnailSkin}
        />
        <Thumbnail
          dataHook={dataHooks.layoutTab.socialLastThumbnail}
          size="tiny"
          selected={socialButtonsFirst === SocialButtonsPlacement.LAST}
          onClick={() => onSocialPresetChange(SocialButtonsPlacement.LAST)}
          hideSelectedIcon
          height={thumbnailSize}
          width={thumbnailSize}
          noPadding
          image={
            <LastIllustration
              width={illustrationSize}
              height={illustrationSize}
              data-illustration={
                isStudio2WdsThumbnailEnabled
                  ? 'IdentityFormLayoutBelow'
                  : undefined
              }
            />
          }
          title={t(
            'memberAuthentication.loginForm.settingsPanel.layout.layoutPreset.socialLast',
          )}
          textPosition="outside"
          skin={thumbnailSkin}
        />
      </FieldSet>
    </SidePanel.Field>
  );
};
